const channel = (color, shift) => (color >> shift) & 0xff;

/**
 * Цвет свечения при этой плотности: плотное светлое, редкое насыщенное
 * @param {number} alpha — от 0 до 1
 * @param {number} innerColor — 0xRRGGBB, у самого контура
 * @param {number} outerColor — 0xRRGGBB, на внешнем краю
 * @returns {number[]} [r, g, b]
 */
export const resolveGlowColor = (alpha, innerColor, outerColor) => [16, 8, 0].map((shift) => {
  const outer = channel(outerColor, shift);
  return Math.round(outer + (channel(innerColor, shift) - outer) * alpha);
});

/**
 * Пиксели мягкого свечения по силуэту картинки. Чистая функция над RGBA-массивом, как
 * и computeOutlinePixels.
 *
 * Три прохода прямоугольного размытия почти неотличимы от гауссова, а считаются за
 * время, которое не зависит от радиуса.
 *
 * @param {Uint8ClampedArray|Uint8Array} srcData — RGBA источника, построчно сверху вниз
 * @param {number} srcWidth
 * @param {number} srcHeight
 * @param {Object} options
 * @param {number} options.pad — запас вокруг источника
 * @param {number} options.blurRadius
 * @param {number} options.blurPasses
 * @param {number} options.gain — усиление: у края силуэта свечение плотнее
 * @param {number} options.falloff — степень спада: чем меньше, тем дальше тянется
 * @param {number} options.innerColor — 0xRRGGBB, у самого контура
 * @param {number} options.outerColor — 0xRRGGBB, на внешнем краю
 * @returns {{data: Uint8Array, width: number, height: number}|null} null — в источнике
 *   нет ни одного непрозрачного пикселя (картинка еще не прочиталась)
 */
export const computeGlowPixels = (srcData, srcWidth, srcHeight, {pad, blurRadius, blurPasses, gain, falloff, innerColor, outerColor}) => {
  if (!srcData || srcWidth <= 0 || srcHeight <= 0) {
    return null;
  }
  const width = srcWidth + pad * 2;
  const height = srcHeight + pad * 2;
  const alpha = new Float32Array(width * height);
  let hasPixels = false;
  for (let y = 0; y < srcHeight; y += 1) {
    for (let x = 0; x < srcWidth; x += 1) {
      const value = srcData[(y * srcWidth + x) * 4 + 3];
      if (value > 0) {
        alpha[(y + pad) * width + x + pad] = value / 255;
        hasPixels = true;
      }
    }
  }
  if (!hasPixels) {
    return null;
  }

  const span = blurRadius * 2 + 1;
  const buffer = new Float32Array(width * height);
  for (let pass = 0; pass < blurPasses; pass += 1) {
    // По строкам
    for (let y = 0; y < height; y += 1) {
      const row = y * width;
      let sum = 0;
      for (let x = -blurRadius; x <= blurRadius; x += 1) {
        sum += alpha[row + Math.min(width - 1, Math.max(0, x))];
      }
      for (let x = 0; x < width; x += 1) {
        buffer[row + x] = sum / span;
        sum += alpha[row + Math.min(width - 1, x + blurRadius + 1)] - alpha[row + Math.max(0, x - blurRadius)];
      }
    }
    // По столбцам
    for (let x = 0; x < width; x += 1) {
      let sum = 0;
      for (let y = -blurRadius; y <= blurRadius; y += 1) {
        sum += buffer[Math.min(height - 1, Math.max(0, y)) * width + x];
      }
      for (let y = 0; y < height; y += 1) {
        alpha[y * width + x] = sum / span;
        sum += buffer[Math.min(height - 1, y + blurRadius + 1) * width + x] - buffer[Math.max(0, y - blurRadius) * width + x];
      }
    }
  }

  const data = new Uint8Array(width * height * 4);
  for (let index = 0; index < alpha.length; index += 1) {
    const value = Math.min(1, (alpha[index] * gain) ** falloff);
    const [r, g, b] = resolveGlowColor(value, innerColor, outerColor);
    const offset = index * 4;
    data[offset] = r;
    data[offset + 1] = g;
    data[offset + 2] = b;
    data[offset + 3] = Math.round(255 * value);
  }
  return {data, width, height};
};
